const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

async function request(path, options = {}) {
    const res = await fetch(`${API}${path}`, {
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
            ...(options.headers || {}),
        },
        ...options,
    });
    if (!res.ok) {
        let detail;
        try {
            detail = (await res.json()).detail;
        } catch {
            detail = res.statusText;
        }
        throw new Error(detail || `Request failed: ${res.status}`);
    }
    if (res.status === 204) return null;
    return res.json();
}

export const api = {
    me: () => request("/auth/me"),
    exchangeSession: (session_id) =>
        request("/auth/session", { method: "POST", body: JSON.stringify({ session_id }) }),
    logout: () => request("/auth/logout", { method: "POST" }),
    listProjects: () => request("/projects"),
    createProject: (name, prompt) =>
        request("/projects", { method: "POST", body: JSON.stringify({ name, prompt }) }),
    getProject: (id) => request(`/projects/${id}`),
    listModels: () => request("/models"),
    listPlans: () => request("/billing/plans"),
    billingMe: () => request("/billing/me"),
    createCheckout: (plan_id, origin_url) =>
        request("/payments/checkout", {
            method: "POST",
            body: JSON.stringify({ plan_id, origin_url }),
        }),
    paymentStatus: (session_id) => request(`/payments/status/${session_id}`),
    renameProject: (id, name) =>
        request(`/projects/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    deleteProject: (id) => request(`/projects/${id}`, { method: "DELETE" }),
    publishProject: (id) => request(`/projects/${id}/publish`, { method: "POST" }),
    unpublishProject: (id) => request(`/projects/${id}/unpublish`, { method: "POST" }),
    dismissStreamingFlag: (id) =>
        request(`/projects/${id}/dismiss-streaming`, { method: "POST" }),
    startGenerationJob: (id, body) =>
        request(`/projects/${id}/generate-job`, { method: "POST", body: JSON.stringify(body) }),
    getJobStatus: (projectId, jobId) =>
        request(`/projects/${projectId}/jobs/${jobId}`),
    cancelJob: (projectId, jobId) =>
        request(`/projects/${projectId}/jobs/${jobId}/cancel`, { method: "POST" }),
    publicMeta: (slug) => request(`/public/${slug}/meta`),
    publicSiteUrl: (slug) => `${API}/public/${slug}`,
    previewUrl: (id) => `${API}/projects/${id}/preview`,
};

// Streams SSE from POST /projects/:id/generate.
// onDelta(text), onHtmlProgress(html), onDone({message,html,updated}), onError(err),
// onInterrupted() — called when the stream ends WITHOUT a `done` event (e.g., ingress cut)
// Optional signal (AbortSignal) — passing controller.abort() cleanly stops the stream.
export async function streamGenerate(projectId, prompt, { model, image_asset_id, target_selector, target_snippet, mode, signal, onDelta, onHtmlProgress, onDone, onError, onInterrupted }) {
    let res;
    try {
        res = await fetch(`${API}/projects/${projectId}/generate`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ prompt, model, image_asset_id, target_selector, target_snippet, mode }),
            signal,
        });
    } catch (e) {
        if (e.name === "AbortError") return;
        onError?.(e);
        return;
    }
    if (!res.ok || !res.body) {
        let detail;
        try { detail = (await res.json()).detail; } catch { detail = res.statusText; }
        onError?.(new Error(detail || `Stream failed: ${res.status}`));
        return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    let sawDone = false;
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            let idx;
            while ((idx = buf.indexOf("\n\n")) !== -1) {
                const chunk = buf.slice(0, idx);
                buf = buf.slice(idx + 2);
                const line = chunk.split("\n").find((l) => l.startsWith("data:"));
                if (!line) continue;
                const payload = line.slice(5).trim();
                if (!payload) continue;
                try {
                    const evt = JSON.parse(payload);
                    if (evt.type === "delta") onDelta?.(evt.content);
                    else if (evt.type === "html_progress") onHtmlProgress?.(evt.html);
                    else if (evt.type === "done") { sawDone = true; onDone?.(evt); }
                    else if (evt.type === "error") onError?.(new Error(evt.message));
                } catch (parseErr) {
                    // malformed SSE frame — skip and keep reading
                    console.warn("streamGenerate: malformed SSE frame", parseErr?.message);
                }
            }
        }
        // Reader ended cleanly but we NEVER saw a `done` event — this means
        // an intermediary (K8s ingress, proxy, etc.) severed the SSE connection.
        // The server's checkpointing has already saved the latest partial HTML to Mongo;
        // we just need to signal the caller so the UI can recover.
        if (!sawDone && !signal?.aborted) onInterrupted?.();
    } catch (e) {
        if (e.name !== "AbortError") onError?.(e);
    }
}


// Job-based generation (no SSE — polls status endpoint every 1.5s).
// Immune to the 60s K8s ingress cap because polls are short-lived requests.
export async function runGenerationJob(projectId, prompt, { model, image_asset_id, target_selector, target_snippet, mode, signal, onProject, onDone, onError, onStatus }) {
    let jobResp;
    try {
        const res = await fetch(`${API}/projects/${projectId}/generate-job`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ prompt, model, image_asset_id, target_selector, target_snippet, mode }),
            signal,
        });
        if (!res.ok) {
            let detail;
            try { detail = (await res.json()).detail; } catch { detail = res.statusText; }
            onError?.(new Error(detail || `Job start failed: ${res.status}`));
            return;
        }
        jobResp = await res.json();
    } catch (e) {
        if (e.name !== "AbortError") onError?.(e);
        return;
    }
    const jobId = jobResp.job_id;
    let cancelled = false;
    signal?.addEventListener("abort", async () => {
        cancelled = true;
        try { await fetch(`${API}/projects/${projectId}/jobs/${jobId}/cancel`, { method: "POST", credentials: "include" }); } catch { /* noop */ }
    });

    let attempts = 0;
    while (!cancelled) {
        attempts += 1;
        await new Promise((r) => setTimeout(r, 1500));
        if (cancelled) break;
        try {
            const [projResp, jobStatus] = await Promise.all([
                fetch(`${API}/projects/${projectId}`, { credentials: "include" }).then((r) => r.json()),
                fetch(`${API}/projects/${projectId}/jobs/${jobId}`, { credentials: "include" }).then((r) => r.json()),
            ]);
            if (projResp?.project) onProject?.(projResp.project);
            if (jobStatus?.status) onStatus?.(jobStatus);
            if (jobStatus?.status === "done") { onDone?.(jobStatus); return; }
            if (jobStatus?.status === "error") { onError?.(new Error(jobStatus.error || "Generation failed")); return; }
            if (jobStatus?.status === "cancelled") return;
        } catch (e) {
            if (attempts > 120) { onError?.(e); return; }
        }
    }
}
